/**
 * ROUTE GUARDS, for a route's `beforeLoad`.
 *
 * They read the session the `_authenticated` layout already resolved into the
 * router context, so a guard never waits on a second request.
 *
 *   requirePerm("admin.staff")   no grant  → access denied, before any request (`app.noAccess`)
 *   requireFeature("cycleCounts") edition drops it → the front door
 *   requireModule("counter")     branch does not run it → the front door
 *
 * A feature or module that is not here is not an error the reader made, so it
 * lands them somewhere useful rather than on a refusal.
 */
import { redirect } from "@tanstack/react-router";
import { featureOn, type Feature } from "@/domain/edition";
import type { ModuleId } from "@/domain/modules";
import { can, type Permission } from "./permissions";
import type { Session } from "./session";

export class AccessDeniedError extends Error {
  readonly permission: Permission;
  constructor(permission: Permission) {
    super(`missing permission ${permission}`);
    this.name = "AccessDeniedError";
    this.permission = permission;
  }
}

interface GuardArgs {
  context: { session: Session };
}

export function requirePerm(permission: Permission) {
  return ({ context }: GuardArgs) => {
    if (!can(context.session, permission)) throw new AccessDeniedError(permission);
  };
}

export function requireFeature(feature: Feature) {
  return ({ context }: GuardArgs) => {
    if (!featureOn(context.session.edition, feature)) throw redirect({ to: "/" });
  };
}

export function requireModule(module: ModuleId) {
  return ({ context }: GuardArgs) => {
    if (!context.session.modules.includes(module)) throw redirect({ to: "/" });
  };
}
